import React from "react";
import { twMerge } from "tailwind-merge";

import Link from "./link";

export type Type = "primary" | "secondary" | "small" | "white" | "icon";

type Props = {
  type: Type;
  children: React.ReactNode;
  link?: Queries.LinkFragment | { uid: string; type: string; lang: string } | string | null;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
};

const Button: React.FC<Props> = ({ type, children, link, className, onClick, disabled = false }) => {
  switch (type) {
    case "primary":
      className = twMerge(
        "group font-roboto font-medium bg-primary-600 h-12 px-6 border border-primary-600 rounded-lg flex flex-row justify-center items-center !text-white text-xs leading-none border-solid",
        "base-transition-150",
        "hover-focus:bg-primary-900 hover-focus:border-primary-900 hover-focus:shadow-button hover-focus:child-span:bg-primary-900",
        "disabled:bg-neutral-600 disabled:cursor-not-allowed disabled:border-neutral-600",
        "child-span:border-b-0",
        className,
      );
      break;
    case "small":
      className = twMerge(
        "group flex font-roboto font-medium mt-4 w-fit border-b border-b-primary-600 base-transition-150 child-span:base-transition-100 animated-link text-primary-600 whitespace-nowrap child-svg:mt-1",
        "hover-focus:text-primary-900 hover-focus:border-primary-900 hover-focus:child-span:text-primary-900",
        "disabled:!text-neutral-600 disabled:cursor-not-allowed disabled:!border-b-neutral-600 disabled:pointer-events-none",
        className,
      );
      break;
    case "secondary":
      className = twMerge(
        "group font-roboto font-medium h-12 px-6 border border-primary-200 rounded-lg flex flex-row justify-center items-center text-xs leading-none base-transition-150 backdrop-blur-[5px] !text-primary-600",
        "hover-focus-active:border-primary-400 hover-focus-active:!text-primary-700",
        "disabled:border-black disabled:!text-neutral-600 disabled:cursor-not-allowed",
        "child-span:border-b-0",
        className,
      );
      break;
    case "white":
      className = twMerge(
        "group font-roboto font-medium h-12 px-6 border border-white bg-white rounded-lg flex flex-row justify-center items-center text-xs base-transition-150 backdrop-blur-[5px] !text-primary-600",
        "hover-focus-active:bg-primary-100 hover-focus-active:border-primary-600 hover-focus-active:shadow-button hover-focus-active:!text-primary-600",
        "disabled:border-black/10 disabled:cursor-not-allowed",
        "child-span:border-b-0",
        className,
      );
      break;
    case "icon":
      className = twMerge(
        "size-6 lg:size-10 bg-neutral-50 rounded-full flex justify-center items-center base-transition-300 text-black",
        "hover-focus-active:bg-black hover-focus-active:text-white",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className,
      );
      break;
    default:
      throw new Error(`Invalid Button Type: ${type as string}`);
  }

  if (!link) {
    return (
      <button onClick={onClick} type="button" className={className} disabled={disabled}>
        {children}
      </button>
    );
  }

  return (
    <Link link={link} onClick={onClick} className={className}>
      {children}
    </Link>
  );
};

export default Button;
